import { styled, Button } from '@mui/material';
import { BASE_BUTTON_COLOR, BASE_BUTTON_VARIANT } from './BaseButton';

type TStyledBaseButton = {
  buttonVariant: BASE_BUTTON_VARIANT;
  buttonColor: BASE_BUTTON_COLOR;
  width?: string;
};

export const StyledBaseButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'buttonVariant' && prop !== 'buttonColor',
})<TStyledBaseButton>(({ buttonVariant, buttonColor, theme, width }) => ({
  padding: '8px',
  textAlign: 'center',
  borderRadius: '8px',
  boxSizing: 'border-box',
  fontWeight: '700',
  fontSize: '1.25rem',
  display: 'flex',
  gap: '10px',
  alignItems: 'center',
  maxWidth: width ? width : 'fit-content',
  minWidth: width ? width : 'fit-content',
  maxHeight: 'fit-content',
  minHeight: 'fit-content',
  ...(buttonVariant === BASE_BUTTON_VARIANT.FILLED && {
    border: 'none',
    ...(buttonColor === BASE_BUTTON_COLOR.PRIMARY && {
      backgroundColor: theme.palette.secondary.darker,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: '#2F3E87',
        border: 'none',
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.SECONDARY && {
      backgroundColor: '#FCEDD1',
      color: '#F27934',
      '&:hover': {
        backgroundColor: '#F9DFAC',
        border: 'none',
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.SUCCESS && {
      backgroundColor: '#088D6B',
      color: theme.palette.common.white,
      border: 'none',
      '&:hover': {
        backgroundColor: '#035E59',
        border: 'none',
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.INHERIT && {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.palette.action.disabledBackground,
        border: 'none',
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.ERROR && {
      backgroundColor: '#D32613',
      color: 'white',
      '&:hover': {
        backgroundColor: '#970912',
        border: 'none',
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.WHITE && {
      backgroundColor: '#E3CE00',
      color: '#212B36',
      '&:hover': {
        backgroundColor: '#E3CE00',
        border: 'none',
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.DOWNLOAD && {
      backgroundColor: '#fff',
      color: '#F27934',
      border: 'solid 2px #F27934',
      '&:hover': {
        backgroundColor: '#fff',
        color: '#F27934',
        border: 'solid 2px #F27934',
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.CONTINUE && {
      backgroundColor: '#F27934',
      color: '#fff',
      border: 'solid 2px #F27934',
      '&:hover': {
        backgroundColor: '#F27934',
        color: '#fff',
        border: 'solid 2px #F27934',
      },
    }),
  }),
  ...(buttonVariant === BASE_BUTTON_VARIANT.OUTLINED && {
    padding: '6px 14px',
    background: 'white',
    ...(buttonColor === BASE_BUTTON_COLOR.PRIMARY && {
      color: theme.palette.secondary.darker,
      borderColor: theme.palette.secondary.darker,
      borderWidth: '2px',
      '&:hover': {
        borderWidth: '2px',
        backgroundColor: '#5F73BC29',
        borderColor: theme.palette.secondary.darker,
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.SECONDARY && {
      color: theme.palette.secondary.dark,
      borderColor: theme.palette.secondary.dark,
      borderWidth: '2px',
      '&:hover': {
        borderWidth: '2px',
        backgroundColor: '#F2793429',
        borderColor: theme.palette.secondary.dark,
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.SUCCESS && {
      color: '#088D6B',
      borderColor: '#088D6B',
      borderWidth: '2px',
      '&:hover': {
        borderWidth: '2px',
        backgroundColor: '#088D6B29',
        borderColor: '#088D6B',
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.INHERIT && {
      color: '#6D6965',
      borderColor: '#6D6965',
      borderWidth: '2px',
      '&:hover': {
        borderWidth: '2px',
        borderColor: '#6D6965',
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.ERROR && {
      color: '#D32613',
      borderColor: '#D32613',
      borderWidth: '2px',
      '&:hover': {
        borderWidth: '2px',
        borderColor: '#D32613',
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.DOWNLOAD && {
      backgroundColor: '#fff',
      color: '#F27934',
      border: 'solid 2px #F27934',
      '&:hover': {
        backgroundColor: '#fff',
        color: '#F27934',
        border: 'solid 2px #F27934',
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.CONTINUE && {
      backgroundColor: '#F27934',
      color: '#fff',
      border: 'solid 2px #F27934',
      '&:hover': {
        backgroundColor: '#F27934',
        color: '#fff',
        border: 'solid 2px #F27934',
      },
    }),
  }),
  ...(buttonVariant === BASE_BUTTON_VARIANT.GHOST && {
    border: 'none',
    ...(buttonColor === BASE_BUTTON_COLOR.PRIMARY && {
      backgroundColor: theme.palette.secondary.light,
      color: theme.palette.secondary.darker,
      '&:hover': {
        backgroundColor: '#9DA9DB',
        border: 'none',
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.SECONDARY && {
      backgroundColor: '#FCEDD1',
      color: '#F27934',
      '&:hover': {
        backgroundColor: '#F9DFAC',
        border: 'none',
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.SUCCESS && {
      backgroundColor: '#D6F1DA',
      color: '#035E59',
      '&:hover': {
        backgroundColor: '#88DAA4',
        border: 'none',
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.INHERIT && {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.text.disabled,
      '&:hover': {
        backgroundColor: theme.palette.action.disabledBackground,
        border: 'none',
      },
    }),
    ...(buttonColor === BASE_BUTTON_COLOR.ERROR && {
      backgroundColor: '#FFD4CF',
      color: '#970912',
      '&:hover': {
        backgroundColor: '#FFBAB2',
        border: 'none',
      },
    }),
  }),
}));
